import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import WorkflowContext from "../../context/WorkflowContext";
import styles from "./GeneralInformation.module.scss";
import { GeneralInformationModel } from "../../../../models/workflow.model";
import { FormikProps, getIn, useFormik } from "formik";
import * as Yup from "yup";
import SpinnerArea from "../../../../components/SpinnerArea";
import SaveSuccessDialog from "../../SaveSuccessDialog";
import CancelDialog from "../../CancelDialog";
import { useNavigate } from "react-router";
import useService from "../../../../hooks/useService";
import workflowService from "../../../../services/workflow.service";
import MainContext from "../../../../context/mainContext";

import SelectMultiple from "../../../../components/SelectMultiple";
import PlaceholderControl from "../../../../components/PlaceholderControl";
import { DocTypeModel } from "../../../../models/docType.model";
import globalDataService from "../../../../services/globalData.service";
import {
  ContinueDialog,
  DocTypesDialog,
  PrioritiesDialog,
} from "./GeneralInforamtionDialogs";
import { IUser } from "../../../../models/user.model";
import userService from "../../../../services/users.service";
import LegalQuestionSection from "../../LegalQuestions";
import ProjectSection from "./ProjectSection";
import {
  PeoplePicker,
  PeoplePickerMultiple,
} from "../../../../components/SelectPicker/PeoplePickers";
import { IPriority } from "../../../../models/priorities.model";
import IconTooltip from "../../../../components/IconTooltip";
import { LangCode } from "../../../../models/language.model";
import { validateBeforeExecute } from "../../../../misc/utils.functions";
import DocumentDownloadButton from "../../../../components/DocumentDownload/DocumentDownloadButton";
import FileUploader, { FileWrapper } from "../../../../components/FileUploader";
import DocumentView from "../../../../components/DocumentView";

const _emptyGeneralInfo: GeneralInformationModel = {
  id: 0,
  title: undefined,
  author: null,
  countries: [],
  docType: {
    name: "",
    definition: "",
    examples: "",
    governance: "",
    levelOfApproval: "DepartmentChief",
    id: 0,
    mandatoryPeerReview: false,
    qualityReview: "",
  },
  languages: [],
  coAuthorInternals: [],
  designatedApprovers: [],
  leadingDepartments: [],
  otherDepartmentsInvolved: [],
  leadingDivisions: [],
  otherDivisionsInvolved: [],
  isAssociatedWithProjects: false,
  targetAudience: undefined,
  abstract: undefined,
  answers: [],
  projects: [],
  isCompleted: false,

  isAligned: false,
  notAlignedReason: "N/A", 
  topics: [],
  stakeholders: [],
  delegates: [],
  priorityTopics: [],
  canEdit: true,
};

interface FormListData {
  languages: LangCode[];
  departments: string[];
  divisions: string[];
  docTypes: DocTypeModel[];
  countries: string[];
  priorities: IPriority[];
  topics: string[];
  priorityTopics: string[];
}

const GeneralInformation: React.FC = () => {
  const { service } = useContext(MainContext);
  const { currentWorkflow, reload, ticketNumber, workflowParentInfo } =
    useContext(WorkflowContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [cancelDialogShow, cancelDialogShowSet] = useState(false);
  const [prioritiesDialogShow, prioritiesDialogSet] = useState(false);
  const [docTypesDialogShow, docTypesDialogShowSet] = useState(false);
  const [continueDialogShow, continueDialogShowSet] = useState(false);

  const [uploadDocument, setUploadDocument] = useState<File | null>();
  const [requiredDocumentError, setRequiredDocumentError] = useState<boolean | null>(null);

  const [model, loading] = useService(async () => {
    return ticketNumber
      ? await workflowService.generalInfo(ticketNumber)
      : await workflowService.generalInfoDefaultData(
        workflowParentInfo?.ticketNumber
      );
  });

  const [formListData, loadingListData] = useService(async () => {
    const response = await Promise.all([
      globalDataService.languages(),
      globalDataService.departments(),
      globalDataService.divisions(),
      globalDataService.docTypes(),
      globalDataService.countries(),
      globalDataService.priorities(),
      globalDataService.topics(),
    ]);
    return {
      languages: response[0],
      departments: response[1],
      divisions: response[2],
      docTypes: response[3],
      countries: response[4],
      priorities: response[5],
      topics: response[6],
      priorityTopics: response[5].map((x) => x.topic),
    } as FormListData;
  });

  const [otherDivisionsOptions, setOtherDivisionsOptions] = useState<string[]>(
    []
  );
  const [otherDepartmentsOptions, setOtherDepartmentsOptions] = useState<
    string[]
  >([]);
  const [levelOfApproval, setLevelOfApproval] = useState<
    "DepartmentChief" | "DivisionChief" | undefined
  >();
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [designatedApproversOptions, setDesignatedApproversOptions] = useState<
    IUser[]
  >([]);
  const [fixedDesignatedApprovers, setFixedDesignatedApprovers] = useState<
    IUser[]
  >([]);

  const [showTopics, setShowTopics] = useState(model?.isAligned);

  const validationSchema = Yup.object().shape({
    notAlignedReason: Yup.string().when("isAligned", {
      is: false,
      then: Yup.string().nullable().required(t("requiredField")!),
      otherwise: Yup.string().nullable().optional(),
    }),
    priorityTopics: Yup.array().when("isAligned", {
      is: true,
      then: Yup.array().min(1, t("requiredField")!),
      otherwise: Yup.array().optional(),
    }),
    title: Yup.string()
      .nullable()
      .required(t("requiredField")!)
      .max(250)
      .matches(
        /^(?!\.)(?!com[0-9]$)(?!con$)(?!lpt[0-9]$)(?!nul$)(?!prn$)[^|*?\\:<>/$"]*[^.|*?\\:<>/$"]+$/,
        t("invalidChareteres")!
      ),
    author: Yup.object().nullable().required(t("requiredField")!),
    coAuthorExternals: Yup.string().nullable().optional().max(2048),
    languages: Yup.array().min(1, t("requiredField")!),
    docType: Yup.object().shape({
      id: Yup.number().integer().moreThan(0, t("requiredField")!).required(),
    }),
    leadingDivisions: Yup.array()
      .when("docType.id", {
        is: (docTypeId: number) => {
          const docTypeSelected = formListData?.docTypes.find(
            (x) => x.id === docTypeId
          );
          return docTypeSelected?.levelOfApproval === "DivisionChief";
        },
        then: Yup.array().min(1, t("requiredField")!),
        otherwise: Yup.array().optional(),
      })
      .when("docType.id", {
        is: (docTypeId: number) => {
          const docTypeSelected = formListData?.docTypes.find(
            (x) => x.id === docTypeId
          );
          return (
            docTypeSelected?.levelOfApproval === "DivisionChief" &&
            docTypeSelected?.mandatoryPeerReview
          );
        },
        then: Yup.array().max(1, t("maxDivisionValidationMessageForDocType")!),
        otherwise: Yup.array().optional(),
      }),
    leadingDepartments: Yup.array().when("docType.id", {
      is: (docTypeId: number) => {
        const docTypeSelected = formListData?.docTypes.find(
          (x) => x.id === docTypeId
        );
        return docTypeSelected?.levelOfApproval === "DepartmentChief";
      },
      then: Yup.array().min(1, t("requiredField")!),
      otherwise: Yup.array().optional(),
    }),
    designatedApprovers: Yup.array().min(1, t("requiredField")!),
    targetAudience: Yup.string().nullable().optional().max(128),
    abstract: Yup.string().nullable().required(t("requiredField")!).max(2046),
    countries: Yup.array().optional(),
    answers: Yup.array().of(
      Yup.object().shape({
        value: Yup.string().when("legalQuestion.required", {
          is: true,
          then: Yup.string().nullable().required(t("requiredField")!).max(2048),
          otherwise: Yup.string().nullable().optional().max(2048),
        }),
      })
    ),
    topics: Yup.array().min(1, t("requiredField")!),
    projects: Yup.array().when("isAssociatedWithProjects", {
      is: true,
      then: Yup.array().min(1, t("requiredField")!),
      otherwise: Yup.array().optional(),
    }),
    
  });

  const formik = useFormik<GeneralInformationModel>({
    initialValues: model || _emptyGeneralInfo,
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const save = (values: GeneralInformationModel, nextStep: boolean) => {
    if (
      values.coAuthorInternals?.some((u) => u.email === values.author?.email)
    ) {
      const validationMsg = t("authorsCoAuthorsValidation");
      formik.setErrors({ coAuthorInternals: validationMsg });
    } else if (!model?.conceptNoteDocument && !uploadDocument) {
      setRequiredDocumentError(true);
    } else {
      service(
        () => {
          if (ticketNumber)
            return workflowService.generalInfoSave(
              ticketNumber!,
              values,
              nextStep,
              uploadDocument
            );
          else if (workflowParentInfo) {
            values.workflowReason = workflowParentInfo.workflowReason;
            return workflowService.modifyPublication(
              workflowParentInfo.ticketNumber,
              values,
              nextStep,
              uploadDocument
            );
          } else return workflowService.startNewPublication(values, nextStep, uploadDocument);
        },
        {
          callback: (response) => {
            if (!nextStep) setSaveSuccess(true);
            else {
              reload();
              navigate(
                `/workflow/${response.ticketNumber}/${response.nextStep}`,
                { relative: "path" }
              );
            }
          },
          loader: setSaving,
        }
      );
    }
  };

  const cancel = (formik: FormikProps<GeneralInformationModel>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  const buildTooltip = (labelKey: string) => (
    <IconTooltip title={t(labelKey)} />
  );

  const optionalLabel = (labelKey: string) => (
    <p>
      {t(labelKey)}
      <em className={styles.optional}>{t("optionalField")}</em>
    </p>
  );

  const onLeadingDivisionsChange = (
    formik: FormikProps<GeneralInformationModel>,
    newValues: string[]
  ) => {
    formik.setFieldValue("leadingDivisions", newValues);

    const otherDivisionsFiltered =
      formik.values.otherDivisionsInvolved.filter(
        (x) => !newValues.includes(x)
      ) || [];
    if (
      otherDivisionsFiltered.toString() !==
      formik.values.otherDivisionsInvolved.toString()
    )
      formik.setFieldValue("otherDivisionsInvolved", otherDivisionsFiltered);

    const filteredOptions =
      formListData?.divisions.filter((x) => !newValues.includes(x)) || [];
    setOtherDivisionsOptions([...filteredOptions]);

    setSelectedDivisions([...newValues, ...otherDivisionsFiltered]);
  };

  const onLeadingDeparmentsChange = (
    formik: FormikProps<GeneralInformationModel>,
    newValues: string[]
  ) => {
    formik.setFieldValue("leadingDepartments", newValues);

    const otherDepartmentsFiltered =
      formik.values.otherDepartmentsInvolved.filter(
        (x) => !newValues.includes(x)
      ) || [];

    if (
      otherDepartmentsFiltered.toString() !==
      formik.values.otherDepartmentsInvolved.toString()
    ) {
      formik.setFieldValue("otherDepartmentsInvolved", [
        ...otherDepartmentsFiltered,
      ]);
    }

    const filteredOptions =
      formListData?.departments.filter((x) => !newValues.includes(x)) || [];
    setOtherDepartmentsOptions([...filteredOptions]);

    setSelectedDepartments([...newValues, ...otherDepartmentsFiltered]);
  };

  useEffect(() => {
    if (model) {
      setIsReadOnly(model.isCompleted || !model.canEdit);

      setShowTopics(model.isAligned);

      if (model.docType.id > 0) {
        setLevelOfApproval(model.docType.levelOfApproval);
      }

      setOtherDepartmentsOptions([
        ...(formListData?.departments.filter(
          (x) => !model.leadingDepartments.includes(x)
        ) || []),
      ]);
      setSelectedDepartments([
        ...model.leadingDepartments,
        ...model.otherDepartmentsInvolved,
      ]);

      setOtherDivisionsOptions([
        ...(formListData?.divisions.filter(
          (x) => !model.leadingDivisions.includes(x)
        ) || []),
      ]);
      setSelectedDivisions([
        ...model.leadingDivisions,
        ...model.otherDivisionsInvolved,
      ]);
    }
  }, [model, formListData]);

  useEffect(() => {
    let active = true;

    const searchChiefs = async () => {
      if (active && model && levelOfApproval) {
        service(
          () =>
            levelOfApproval === "DepartmentChief"
              ? userService.departmentChiefs(selectedDepartments)
              : userService.divisionChiefs(selectedDivisions),
          {
            callback: (data) => {
              setDesignatedApproversOptions(data);

              const filteredApprovers =
                formik.values.designatedApprovers?.filter((x) =>
                  data.some(
                    (c) => x.email.toLowerCase() === c.email.toLowerCase()
                  )
                ) || [];

              formik.setFieldValue("designatedApprovers", [
                ...filteredApprovers,
              ]);
              active = false;
            },
          }
        );
      }
    };

    searchChiefs();

    return () => {
      active = false;
    };
  }, [model, levelOfApproval, selectedDepartments, selectedDivisions]);

  useEffect(() => {
    const fixedOptions =
      levelOfApproval === "DepartmentChief"
        ? designatedApproversOptions.filter((x) => {
          const department = x.department!.split("/")[0];
          return formik.values.leadingDepartments.includes(department);
        })
        : designatedApproversOptions.filter((x) =>
          formik.values.leadingDivisions.includes(x.department!)
        );

    setFixedDesignatedApprovers(fixedOptions);
  }, [
    designatedApproversOptions,
    formik.values.leadingDepartments,
    formik.values.leadingDivisions,
  ]);


  const hasParent = Boolean(workflowParentInfo || currentWorkflow?.workflowType === "Modification");

  return (
    <SpinnerArea loading={loading || loadingListData || saving}>
      <form
        onSubmit={formik.handleSubmit}
        onReset={formik.handleReset}
        className={styles.generalInformationForm}
      >
        <Grid container>
          <Grid item sm={12}>
            <div className={styles.header}>
              <p>
                <strong>{t("publicationTicketNumber")}: </strong>
                {currentWorkflow?.ticketNumber ??
                  workflowParentInfo?.ticketNumber}
              </p>
              <p>
                <strong>{t("generalInformation")}</strong>
                <em>{t("generalInformationTooltip")}</em>
              </p>
            </div>
          </Grid>
        </Grid>
        <div className={styles.formContainer}>
          <div
            className={`${styles["area-aligned"]} ${styles.radiobutonsControl}`}
          >
            <FormLabel
              id="isAligned-group-label"
              className={styles.alignedLabel}
            >
              <p>
                {t("isThisPublicationAligned")}
                {/* <Button
                  variant="text"
                  onClick={() => prioritiesDialogSet(true)}
                >
                  {t("viewMore")}
                </Button> */}
              </p>
            </FormLabel>

            <RadioGroup
              aria-labelledby="isAligned-group-label"
              name="isAligned"
              row
              value={formik.values.isAligned}
              onChange={(e) => {
                const newValue = e.currentTarget.value === "true";
                formik.setFieldValue("isAligned", newValue);
                setShowTopics(newValue);
                if (!newValue) formik.setFieldValue("priorityTopics", []);
                else formik.setFieldValue("notAlignedReason", "N/A");
              }}
              onBlur={formik.handleBlur}
            >
              <FormControlLabel
                value={true}
                control={<Radio inputProps={{ disabled: true }} />}
                label={t("yes")}
              />
              <FormControlLabel
                value={false}
                control={<Radio inputProps={{ disabled: isReadOnly }} />}
                label={t("no")}
              />
            </RadioGroup>
          </div>
          <div className={styles["area-reason-priorities"]}>
            {showTopics ? (
              <FormControl fullWidth>
                <FormLabel id="priorityTopics-label">
                  {t("priorityTopicsLabel")}
                </FormLabel>
                <SelectMultiple
                  id="priorityTopics"
                  placeholder={t("priorityTopicsLabel").toString()}
                  currentValues={formik.values.priorityTopics || []}
                  keySelector={(x) => x}
                  labelSelector={(x) => x}
                  source={formListData?.priorityTopics || []}
                  readOnly={isReadOnly}
                  onChange={(newValues) =>
                    formik.setFieldValue("priorityTopics", newValues)
                  }
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.priorityTopics &&
                    formik.errors.priorityTopics
                  }
                />
              </FormControl>
            ) : (
              <FormControl fullWidth>
                <FormLabel id="notAlignedReason-label">
                  <p>
                    {t("notAlignedReason")}
                    <em>({t("notAlignedReasonEnable")})</em>
                  </p>
                </FormLabel>
                <TextField
                  id="notAlignedReason"
                  name="notAlignedReason"
                  placeholder={t("notAlignedReasonEnablePlaceholder") || ""}
                  fullWidth
                  inputProps={{ readOnly: true }} 
                  value={formik.values.notAlignedReason || "N/A"}                  
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.notAlignedReason &&
                    formik.errors.notAlignedReason
                  }
                  error={
                    formik.touched.notAlignedReason &&
                    Boolean(formik.errors.notAlignedReason)
                  }
                />
              </FormControl>
            )}
          </div>
          <div className={styles["area-3"]}></div>

          <div className={styles["area-title"]}>
            <FormControl fullWidth>
              <FormLabel id="title-label">{t("title")}</FormLabel>
              <TextField
                id="title"
                name="title"
                placeholder={t("title") || ""}
                fullWidth
                inputProps={{
                  readOnly: isReadOnly || hasParent,
                }}
                value={formik.values.title || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={hasParent}
                helperText={formik.touched.title && formik.errors.title}
                error={formik.touched.title && Boolean(formik.errors.title)}
              />
            </FormControl>
          </div>
          <div className={styles["area-author"]}>
            <FormControl fullWidth>
              <FormLabel id="author-label">
                {t("author")}
                {buildTooltip("authorTooltip")}
              </FormLabel>

              <PeoplePicker
                id="author"
                defaultValue={model?.author || null}
                readOnly={isReadOnly}
                placeholder={t("author").toString()}
                onChange={(newValue) => {
                  formik.setFieldValue("author", newValue);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.author && formik.errors.author}
              />
            </FormControl>
          </div>
          <div className={styles["area-coauthor-externals"]}>
            <FormControl fullWidth>
              <FormLabel id="externalCoAuthors-label">
                {optionalLabel("externalCoAuthorsLabel")}
              </FormLabel>
              <TextField
                id="coAuthorExternals"
                name="coAuthorExternals"
                placeholder={t("externalCoAuthorsLabel") || ""}
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
                value={formik.values.coAuthorExternals || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.coAuthorExternals &&
                  formik.errors.coAuthorExternals
                }
                error={
                  formik.touched.coAuthorExternals &&
                  Boolean(formik.errors.coAuthorExternals)
                }
              />
            </FormControl>
          </div>
          <div className={styles["area-coauthor-internals"]}>
            <FormControl fullWidth>
              <FormLabel id="internalCoAuthors-label">
                {optionalLabel("internalCoAuthorsLabel")}
                {buildTooltip("internalCoAuthorsTooltip")}
              </FormLabel>
              <PeoplePickerMultiple
                id="coAuthorInternals"
                defaultValue={model?.coAuthorInternals || []}
                readOnly={isReadOnly}
                placeholder={t("internalCoAuthorsLabel").toString()}
                onChange={(newValue) => {
                  formik.setFieldValue("coAuthorInternals", newValue);
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.coAuthorInternals &&
                  formik.errors.coAuthorInternals
                }
              />
            </FormControl>
          </div>
          <div className={styles["area-topics"]}>
            <FormControl fullWidth>
              <FormLabel id="topics-label">{t("topicsLabel")}</FormLabel>
              <SelectMultiple
                id="topics"
                placeholder={t("topicsLabel").toString()}
                currentValues={formik.values.topics || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={formListData?.topics || []}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  formik.setFieldValue("topics", newValues)
                }
                onBlur={formik.handleBlur}
                error={formik.touched.topics && formik.errors.topics}
              />
            </FormControl>
          </div>

          <div className={styles["area-languages"]}>
            <FormControl fullWidth>
              <FormLabel id="languages-label">{t("mainLanguages")}</FormLabel>
              <SelectMultiple
                id="languages"
                placeholder={t("selectLanguages").toString()}
                currentValues={formik.values.languages || []}
                keySelector={(x) => x}
                labelSelector={(x) => t(`languages.${x}`)}
                source={formListData?.languages || []}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  formik.setFieldValue("languages", newValues)
                }
                onBlur={formik.handleBlur}
                error={formik.touched.languages && formik.errors.languages}
              />
            </FormControl>
          </div>
          <div className={styles["area-doc-type"]}>
            <FormControl fullWidth>
              <FormLabel id="docType-label">
                {t("docType")}{" "}
                <Button
                  variant="text"
                  onClick={() => docTypesDialogShowSet(true)}
                >
                  {t("viewDocTypeExamples")}
                </Button>
              </FormLabel>
              <Select
                id="docType.id"
                name="docType.id"
                disabled={hasParent}
                className={
                  getIn(formik.touched, "docType.id") &&
                    getIn(formik.errors, "docType.id")
                    ? "Mui-error"
                    : ""
                }
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
                value={formik.values.docType.id.toString()}
                onChange={(event: SelectChangeEvent) => {
                  formik.setFieldValue("docType.id", event.target.value);

                  setTimeout(() => {
                    const docTypeSelected = formListData?.docTypes.find(
                      (x) => x.id === parseInt(event.target.value)
                    )!;
                    setLevelOfApproval(docTypeSelected.levelOfApproval);
                    switch (docTypeSelected.levelOfApproval) {
                      case "DepartmentChief":
                        formik.setFieldValue("leadingDivisions", []);
                        formik.setFieldValue("otherDivisionsInvolved", []);
                        formik.setFieldValue("designatedApprovers", []);
                        setSelectedDivisions([]);
                        break;
                      case "DivisionChief":
                        formik.setFieldValue("leadingDepartments", []);
                        formik.setFieldValue("otherDepartmentsInvolved", []);
                        formik.setFieldValue("designatedApprovers", []);
                        setSelectedDepartments([]);
                        break;
                    }
                  }, 500);
                }}
                onBlur={formik.handleBlur}
                displayEmpty
                renderValue={(value) =>
                  value !== "0" ? (
                    formListData?.docTypes.find((x) => x.id === parseInt(value))
                      ?.name
                  ) : (
                    <PlaceholderControl text={t("docType")} />
                  )
                }
              >
                <MenuItem
                  key={`doc_types_${0}`}
                  value={0}
                  className={"selectPlaceholder"}
                >
                  {t("docType")}
                </MenuItem>
                {formListData?.docTypes.map((x) => (
                  <MenuItem key={`doc_types_${x.id}`} value={x.id!}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText className="Mui-error">
                {getIn(formik.touched, "docType.id") &&
                  getIn(formik.errors, "docType.id")}
              </FormHelperText>
            </FormControl>
          </div>
          <div className={styles["area-leading-divisions"]}>
            <FormControl fullWidth>
              <FormLabel id="leadingDivisions-label">
                {t("leadingDivisions")}{" "}
                {buildTooltip("leadingDivisionsTooltip")}
              </FormLabel>
              <SelectMultiple
                id="leadingDivisions"
                placeholder={t("selectLeadingDivisions").toString()}
                currentValues={formik.values.leadingDivisions || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={formListData?.divisions || []}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  onLeadingDivisionsChange(formik, newValues)
                }
                disabled={levelOfApproval !== "DivisionChief"}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.leadingDivisions &&
                  formik.errors.leadingDivisions
                }
              />
            </FormControl>
          </div>
          <div className={styles["area-others-divisions"]}>
            <FormControl fullWidth>
              <FormLabel id="otherDivisionsInvolved-label">
                {optionalLabel("otherDivisionsInvolved")}
                {buildTooltip("otherDivisionsInvolvedTooltip")}
              </FormLabel>
              <SelectMultiple
                id="otherDivisionsInvolved"
                placeholder={t("selectOtherDivisionsInvolved").toString()}
                currentValues={formik.values.otherDivisionsInvolved || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={otherDivisionsOptions}
                readOnly={isReadOnly}
                onBlur={formik.handleBlur}
                onChange={(newValues) => {
                  formik.setFieldValue("otherDivisionsInvolved", [
                    ...newValues,
                  ]);
                  setSelectedDivisions([
                    ...formik.values.leadingDivisions,
                    ...newValues,
                  ]);
                }}
                disabled={levelOfApproval !== "DivisionChief"}
              />
            </FormControl>
          </div>
          <div className={styles["area-leading-departments"]}>
            <FormControl fullWidth>
              <FormLabel id="leadingDeparments-label">
                {t("leadingDepartments")}{" "}
                {buildTooltip("leadingDepartmentsTooltip")}
              </FormLabel>
              <SelectMultiple
                id="leadingDepartments"
                placeholder={t("selectLeadingDepartments").toString()}
                currentValues={formik.values.leadingDepartments || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={formListData?.departments || []}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  onLeadingDeparmentsChange(formik, newValues)
                }
                disabled={levelOfApproval !== "DepartmentChief"}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.leadingDepartments &&
                  formik.errors.leadingDepartments
                }
              />
            </FormControl>
          </div>
          <div className={styles["area-others-departments"]}>
            <FormControl fullWidth>
              <FormLabel id="otherDepartmentsInvolved-label">
                {optionalLabel("otherDepartmentsInvolved")}
                {buildTooltip("otherDepartmentsInvolvedTooltip")}
              </FormLabel>
              <SelectMultiple
                id="otherDepartmentsInvolved"
                placeholder={t("selectOtherDepartmentsInvolved").toString()}
                currentValues={formik.values.otherDepartmentsInvolved || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={otherDepartmentsOptions}
                readOnly={isReadOnly}
                onChange={(newValues) => {
                  formik.setFieldValue("otherDepartmentsInvolved", newValues);
                  setSelectedDepartments([
                    ...formik.values.leadingDepartments,
                    ...newValues,
                  ]);
                }}
                disabled={levelOfApproval !== "DepartmentChief"}
                onBlur={formik.handleBlur}
              />
            </FormControl>
          </div>
          <div className={styles["area-designated-approver"]}>
            <FormControl fullWidth>
              <FormLabel id="designatedApprovers-label">
                {t("designatedApprovers")}{" "}
                {buildTooltip("designatedApproversTooltip")}
              </FormLabel>
              <SelectMultiple
                id="designatedApprovers"
                fixedValues={fixedDesignatedApprovers}
                placeholder={t("selectDesignatedApprovers").toString()}
                currentValues={[...formik.values.designatedApprovers!] || []}
                keySelector={(x) => `${x.department}-${x.email}`}
                labelSelector={(x) =>
                  t(
                    levelOfApproval === "DepartmentChief"
                      ? "designatedApproverDepartment"
                      : "designatedApproverDivision",
                    { department: x.department, displayName: x.displayName }
                  )
                }
                source={designatedApproversOptions}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  formik.setFieldValue("designatedApprovers", newValues)
                }
                onBlur={formik.handleBlur}
                error={
                  formik.touched.designatedApprovers &&
                  formik.errors.designatedApprovers
                }
              />
            </FormControl>
          </div>
          <div className={styles["area-audience"]}>
            <FormControl fullWidth>
              <FormLabel id="targetAudience-label">
                {optionalLabel("targetAudience")}
              </FormLabel>
              <TextField
                id="targetAudience"
                name="targetAudience"
                placeholder={t("targetAudience") || ""}
                fullWidth
                inputProps={{ readOnly: isReadOnly }}
                value={formik.values.targetAudience || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={
                  formik.touched.targetAudience && formik.errors.targetAudience
                }
                error={
                  formik.touched.targetAudience &&
                  Boolean(formik.errors.targetAudience)
                }
              />
            </FormControl>
          </div>

          <div className={styles["area-countries"]}>
            <FormControl fullWidth>
              <FormLabel id="countries-label">
                {optionalLabel("countriesLabel")}
                {buildTooltip("countriesTooltip")}
              </FormLabel>
              <SelectMultiple
                id="countries"
                placeholder={t("selectCountries").toString()}
                currentValues={formik.values.countries || []}
                keySelector={(x) => x}
                labelSelector={(x) => x}
                source={formListData?.countries || []}
                readOnly={isReadOnly}
                onChange={(newValues) =>
                  formik.setFieldValue("countries", newValues)
                }
                onBlur={formik.handleBlur}
                error={formik.touched.countries && formik.errors.countries}
              />
            </FormControl>
          </div>

          <div className={styles["area-projects"]}>
            <FormControl fullWidth className={styles.isAssociatedWithProjects}>
              <FormLabel id="isAssociatedWithProjects-label">
                {t("isAssociatedWithMaestro")}
              </FormLabel>
              <RadioGroup
                aria-labelledby="isAssociatedWithProjects-group-label"
                name="isAssociatedWithProjects"
                row
                value={formik.values.isAssociatedWithProjects}
                onChange={(e) => {
                  const newValue = e.currentTarget.value === "true";
                  formik.setFieldValue("isAssociatedWithProjects", newValue);

                  if (!newValue) {
                    setTimeout(() => {
                      formik.setFieldValue("projects", []);
                      formik.validateField("projects");
                    }, 100);
                  }
                }}
                onBlur={formik.handleBlur}
              >
                <FormControlLabel
                  value={"true"}
                  control={<Radio inputProps={{ disabled: isReadOnly }} />}
                  label={t("yes")}
                />
                <FormControlLabel
                  value={"false"}
                  control={<Radio inputProps={{ disabled: isReadOnly }} />}
                  label={t("no")}
                />
              </RadioGroup>
            </FormControl>

            <ProjectSection
              currentProjects={formik.values.projects}
              onBlur={formik.handleBlur}
              isReadOnly={isReadOnly}
              visible={formik.values.isAssociatedWithProjects}
              onAdd={(newProject) => {
                formik.setFieldValue("projects", [
                  ...formik.values.projects,
                  newProject,
                ]);
              }}
              onRemove={(index: number) => {
                const filteredProjects = [...formik.values.projects];
                filteredProjects.splice(index, 1);

                formik.setFieldValue("projects", [...filteredProjects]);
              }}
            />
          </div>
          <div className={styles["area-abstract"]}>
            <FormControl fullWidth>
              <FormLabel id="description-label">
                <p>
                  {t("generalInfoDescription")}
                  <em>{t("generalInfoDescriptionHelpText")}</em>
                </p>
              </FormLabel>
              <TextField
                id="abstract"
                name="abstract"
                placeholder={t("description") || ""}
                fullWidth
                rows={3}
                multiline
                inputProps={{ readOnly: isReadOnly }}
                value={formik.values.abstract || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.abstract && formik.errors.abstract}
                error={
                  formik.touched.abstract && Boolean(formik.errors.abstract)
                }
              />
            </FormControl>
          </div>

          <div className={styles["concept-note-title"]}>
            <FormControl fullWidth>
              <FormLabel id="description-label">
                <p>
                  {t("downloadUploadConceptNote")}
                </p>
              </FormLabel>
            </FormControl>
          </div>
          <div className={styles["area-concept-note-upload"]}>
            <FileUploader
              title={t("uploadConceptNote")!}
              onChange={(file: File | File[] | FileWrapper[]) => {
                setUploadDocument(file as File);
              }}
              allowedExtensions={[".doc", ".docx"]}
              isReadOnly={isReadOnly}
              errorValidate={requiredDocumentError}
            />
          </div>
          <div className={styles["area-concept-note-template"]}>
            <FormControl fullWidth>
              <FormLabel id="description-label">
                <p>
                  {t("conceptNoteTemplate")}
                </p>
              </FormLabel>
            </FormControl>
            <div className={styles["download-document"]}>
              <FormControl fullWidth>
                <p>{t("conceptNoteTemplate")}</p>
                {<DocumentDownloadButton
                  templateCode="CONCEPT_NOTE_TEMPLATE"
                  text="Template"
                  type="icon"
                />}
              </FormControl>
            </div>
          </div>
          <div className={styles["area-concept-note-download"]}>
            {model?.conceptNoteDocument && (
              <div>
                <DocumentView document={model.conceptNoteDocument} hideOpenOnline={true} />
              </div>
            )}
          </div>

          <div className={styles["area-stakeholders"]}>
            <FormControl fullWidth>
              <FormLabel id="stakeholders-label">
                {optionalLabel("stakehodersLabel")}
              </FormLabel>
              <PeoplePickerMultiple
                id="stakeholders"
                defaultValue={model?.stakeholders || []}
                readOnly={isReadOnly}
                placeholder={"CC"}
                onChange={(newValue) => {
                  formik.setFieldValue("stakeholders", newValue);
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.stakeholders && formik.errors.stakeholders
                }
              />
            </FormControl>
          </div>

          <div className={styles["area-delegates"]}>
            <FormControl fullWidth>
              <FormLabel id="delegates-label">
                {optionalLabel("delegatedUsersLabel")}
              </FormLabel>
              <PeoplePickerMultiple
                id="delegates"
                defaultValue={model?.delegates || []}
                readOnly={isReadOnly}
                placeholder={"CC"}
                onChange={(newValue) => {
                  formik.setFieldValue("delegates", newValue);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.delegates && formik.errors.delegates}
              />
            </FormControl>
          </div>

          <div className={styles["area-legal-questions"]}>
            <LegalQuestionSection
              answers={formik.values.answers}
              propertyName={"answers"}
              isReadOnly={isReadOnly}
              formik={formik}
            />
          </div>

          <div className={styles["area-artificial-intelligence-legend"]}>
            <p id="priorityTopics-label">
              {t("artificialIntelligenceLegend1")}
            </p>
          </div>
        </div>
        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>
          <Button
            className={styles.save}
            disabled={isReadOnly}
            onClick={() =>
              validateBeforeExecute(formik, () => save(formik.values, false))
            }
          >
            {t("save")}
          </Button>
          <Button
            className={styles.continue}
            disabled={isReadOnly}
            onClick={() => {
              if (!model?.conceptNoteDocument && !uploadDocument) {
                setRequiredDocumentError(true);
                validateBeforeExecute(formik, () => console.log("Validate upload File and formik"));
              } else {
                validateBeforeExecute(formik, () => continueDialogShowSet(true));
              }
            }}
          >
            {t("continue")}
          </Button>
        </div>
      </form>
      <SaveSuccessDialog open={saveSuccess} />
      <CancelDialog
        open={cancelDialogShow}
        yesAction={() => navigate(-1)}
        noAction={() => cancelDialogShowSet(false)}
      />
      <ContinueDialog
        open={continueDialogShow}
        setOpen={continueDialogShowSet}
        action={() => save(formik.values, true)}
      />
      <DocTypesDialog
        docTypes={formListData?.docTypes}
        open={docTypesDialogShow}
        setOpen={docTypesDialogShowSet}
      />
      <PrioritiesDialog
        open={prioritiesDialogShow}
        setOpen={prioritiesDialogSet}
        priorities={formListData?.priorities!}
      />
    </SpinnerArea>
  );
};

export default GeneralInformation;
